import { useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';

import styles from './AddAccessRights.module.scss';
import { IAddAccessRightsProps } from './IAddAccessRightsProps';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { CountryBrandRightRequestModel } from '../../../models/countryBrandRights/CountryBrandRightRequestModel';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';

const AddAccessRights = ({ refetch }: IAddAccessRightsProps): JSX.Element => {
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [countriesIds, setCountriesIds] = useState<number[] | undefined>(
		undefined
	);
	const [brandsIds, setBrandsIds] = useState<number[]>([]);
	const [competitorsIds, setCompetitorsIds] = useState<number[]>([]);

	const [usersOptions, setUsersOptions] = useState<DropdownOption[]>([]);
	const [brandsOptions, setBrandsOptions] = useState<DropdownOption[]>([]);
	const [countriesOptions, setCountiresOptions] = useState<DropdownOption[]>(
		[]
	);

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [onlyCompetitors, setOnlyCompetitors] = useState<boolean>(false);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>(`users/dropdown`, undefined)
			.then((options) => setUsersOptions(options))
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(`brands/dropdown`, undefined)
			.then((options) => setBrandsOptions(options))
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(`countries/dropdown`, undefined)
			.then((options) => setCountiresOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	useEffect(() => {
		const htmlRootElement = document.querySelector('html');

		if (isVisible) {
			htmlRootElement!.style.overflow = 'hidden';
		} else {
			htmlRootElement!.style.overflow = 'initial';
		}
	}, [isVisible]);

	const resetDialog = (): void => {
		setUserId(undefined);
		setBrandsIds([]);
		setCompetitorsIds([]);
		setCountriesIds(undefined);
		setIsVisible(false);
		setIsLoading(false);
	};

	const handleAddAccessRights = async (): Promise<void> => {
		if (
			!userId ||
			(brandsIds.length == 0 && competitorsIds.length == 0) ||
			!countriesIds
		) {
			return;
		}

		setIsLoading(true);

		const countryBrandRight: CountryBrandRightRequestModel = {
			userId,
			brandsIds: brandsIds?.length > 0 ? brandsIds : competitorsIds,
			countriesIds
		};

		try {
			await httpService.post(
				'countryBrandRights',
				JSON.stringify(countryBrandRight),
				undefined,
				false
			);

			resetDialog();
		} catch (err) {
			notifierService.error(err);
		} finally {
			refetch();
		}
	};

	const addDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleAddAccessRights}
				disabled={
					!userId ||
					(brandsIds.length == 0 && competitorsIds.length == 0) ||
					!countriesIds
				}
			>
				Add
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles.dialogFooter}>
			<p>Adding user access rights...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				className={`${styles.addButton} btn primary`}
				onClick={() => setIsVisible(true)}
			>
				Add User Access Rights
			</button>

			<Dialog
				className={styles.dialog}
				header='Add User Access Rights'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles.dialogContent}>
					<label htmlFor='userId'>User</label>
					<Dropdown
						id='userId'
						required
						filter
						placeholder='Select User'
						value={userId}
						options={usersOptions}
						onChange={(e: DropdownChangeParams) => {
							setUserId(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='countriesId'>Countries</label>
					<MultiSelect
						id='countriesId'
						required
						filter
						placeholder='Select Countries'
						value={countriesIds}
						options={countriesOptions}
						onChange={(e: MultiSelectChangeParams) => {
							setCountriesIds(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label
						style={{
							display: 'flex',
							justifyContent: 'space-between'
						}}
					>
						<span>Brands</span>
					</label>
					<MultiSelect
						id='brandsIds'
						required
						filter
						placeholder='Select Brands'
						value={brandsIds}
						options={brandsOptions}
						onChange={(e: MultiSelectChangeParams) => {
							setBrandsIds(e.target.value);
							setCompetitorsIds([]);
							setOnlyCompetitors(false);
						}}
					/>
				</div>
			</Dialog>
		</>
	);
};

export default AddAccessRights;
